import { useMemo } from "react";
import type { Segment } from "@memento/types/segment";
import type { Card, CardIdentifier } from "@memento/types/card";
import type { Body as UpdateCardBody } from "@memento/app/endpoints/card/update";
import { RenderCreate } from "@memento/types/render";
import { useAuthenticatedFetcher } from "src/libs/fetcher";
import { useAuth } from "src/hooks/useAuth";
import { useSendAnalyticsEvent } from "src/libs/analytics-lib";
import { toastError, toastSuccess } from "src/libs/toast-lib";
import { getPlatform } from "src/libs/browser-lib";
import { sendRenderStartedAnalyticsEvent } from "src/libs/compilation-lib";
import { addAppAndApiVersions } from "src/libs/api-endpoint-lib";
import { TierLabel } from "@memento/types/subscription";
import * as SaveSegmentsEndpoint from "@memento/app/endpoints/segment/update-many";

export type CreateCardProps = {
  cardUrl: string;
  title: string;
  username: string;
  description: string;
  dueDate: number;
  attachment: null | string;
  dripInfo?: {
    email: string;
    encodedGuestUserInfo?: string;
  };
  featureImageSettings: {
    crop: boolean;
  };
  occasionId: string;
  themeId: string;
};

export type UseLegacyRequest = ReturnType<typeof useLegacyRequest>;

export function useLegacyRequest() {
  const { user } = useAuth();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const fetcher = useAuthenticatedFetcher();

  const requests = useMemo(() => {
    return {
      getCards: async () => {
        return fetcher(addAppAndApiVersions("/cards"));
      },
      createCard: async (cardData: CreateCardProps) => {
        return fetcher(addAppAndApiVersions("/cards"), {
          method: "POST",
          body: { ...cardData, platform: getPlatform() },
        });
      },
      saveCard: async (
        { cardId, cardUrl }: CardIdentifier,
        data: UpdateCardBody
      ) => {
        return fetcher(addAppAndApiVersions(`/cards/${cardUrl}/${cardId}`), {
          method: "PUT",
          body: data,
        });
      },
      saveSegment: async (segment: Segment) => {
        const { segmentId, cardId, ...segmentData } = segment;

        return fetcher(
          addAppAndApiVersions(`/segments/${cardId}/${segmentId}`),
          {
            method: "PUT",
            body: segmentData,
          }
        );
      },
      saveSegments: async (
        { cardId }: SaveSegmentsEndpoint.PathParams,
        body: SaveSegmentsEndpoint.Body
      ): Promise<void> => {
        await fetcher(addAppAndApiVersions(`/segments/${cardId}`), {
          method: "PUT",
          body,
        });

        return;
      },
      saveSegmentMeta: async (card: Card, videoId: string, email: string) => {
        return fetcher(
          addAppAndApiVersions(
            `/cards/${card.cardUrl}/${card.cardId}/segments/meta/${videoId}`
          ),
          {
            method: "PUT",
            body: { email },
          }
        );
      },
      removeCardCollaborator: async (card: Card, username: string) => {
        return fetcher(
          `/cards/${card.cardUrl}/${card.cardId}/collaborators/remove`,
          { method: "PUT", body: { username } }
        );
      },
      addCardCollaborator: async (card: Card, username: string) => {
        return fetcher(
          addAppAndApiVersions(
            `/cards/${card.cardUrl}/${card.cardId}/collaborators/add`
          ),
          { method: "PUT", body: { username } }
        );
      },
      searchCardCollaborators: async (card: Card, email: string) => {
        return fetcher(
          addAppAndApiVersions(
            `/cards/${card.cardUrl}/${card.cardId}/collaborators/search`
          ),
          { method: "POST", body: { email } }
        );
      },
      setErrorMessage: (message: string) => {
        toastError(message);
      },
      setSuccessMessage: (message: string) => {
        toastSuccess(message);
      },
      isFacebookUser: () => {
        return Boolean(user?.username && user?.username.startsWith("Facebook"));
      },
      startRenderingV2: async (
        renderInfo: RenderCreate,
        username: string | undefined,
        tierLabel: TierLabel | undefined,
        analyticsData: {
          numberOfVideos: number;
          numberOfPhotos: number;
          numberOfContributors: number;
          numberOfInvites: number;
        }
      ) => {
        return fetcher(addAppAndApiVersions(`/render`), {
          method: "POST",
          body: renderInfo,
        })
          .then(() => {
            sendRenderStartedAnalyticsEvent({
              compilationVersion: 2,
              cardCompilations: renderInfo.cardCompilations,
              username,
              tierLabel,
              cardId: renderInfo.cardId,
              numberOfVideos: analyticsData.numberOfVideos,
              numberOfPhotos: analyticsData.numberOfPhotos,
              numberOfContributors: analyticsData.numberOfContributors,
              numberOfInvites: analyticsData.numberOfInvites,
            });
          })
          .catch((err) => {
            console.log(err);
          });
      },
      deleteCard: async (card: Card) => {
        return fetcher(
          addAppAndApiVersions(`/cards/${card.cardUrl}/${card.cardId}`),
          {
            method: "DELETE",
          }
        )
          .then(() => {
            sendAnalyticsEvent({
              event: "GroupVideoDeleted",
            });
          })
          .catch((err) => {
            console.log(err);
          });
      },
      updateUserPref: async (
        username: string,
        prefName: string,
        prefValue: any
      ) => {
        return fetcher(addAppAndApiVersions(`/user/${username}/prefs/set`), {
          method: "POST",
          body: { keyname: prefName, value: prefValue },
        });
      },
    };
  }, [fetcher, user, sendAnalyticsEvent]);

  return requests;
}
